import { useState, useEffect, useRef } from 'react';
import { useCMSAuth } from '@/context/CMSAuthContext';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  MessageCircle,
  Users,
  Bot,
  Headphones,
  Send,
  RefreshCw,
  User,
  Clock,
  CheckCircle,
  Phone,
  Mail,
  MapPin,
  TrendingUp,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const CounselorDashboard = () => {
  const { token, user } = useCMSAuth();
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [filterMode, setFilterMode] = useState('all');
  const [counselors, setCounselors] = useState([]);
  const [isOnline, setIsOnline] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [selectedSession?.messages]);

  const fetchData = async () => {
    try {
      const [sessionsRes, statsRes, counselorsRes] = await Promise.all([
        fetch(`${API_URL}/api/chat/sessions?${filterMode !== 'all' ? `mode=${filterMode}` : ''}`),
        fetch(`${API_URL}/api/chat/stats`),
        fetch(`${API_URL}/api/chat/counselors`)
      ]);

      if (sessionsRes.ok) {
        const data = await sessionsRes.json();
        setSessions(data.sessions || []);
      }
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
      if (counselorsRes.ok) {
        const data = await counselorsRes.json();
        setCounselors(data.counselors || []);
      }
    } catch (e) {
      console.error('Failed to fetch data:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Poll for updates every 10 seconds
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [filterMode]);

  const selectSession = async (session) => {
    setSelectedSession(session);
    // Fetch full session details
    try {
      const res = await fetch(`${API_URL}/api/chat/session/${session.id}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedSession(data);
      }
    } catch (e) {
      console.error('Failed to fetch session:', e);
    }
  };

  const sendReply = async () => {
    if (!replyMessage.trim() || !selectedSession) return;

    try {
      const res = await fetch(`${API_URL}/api/chat/counselor/message?session_id=${selectedSession.id}&content=${encodeURIComponent(replyMessage)}&counselor_id=${user?.id || 'admin'}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        setReplyMessage('');
        // Refresh session
        selectSession(selectedSession);
        toast.success('Message sent');
      }
    } catch (e) {
      console.error('Failed to send reply:', e);
      toast.error('Failed to send message');
    }
  };

  const captureLead = async (sessionId) => {
    try {
      const res = await fetch(`${API_URL}/api/chat/capture-lead/${sessionId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Lead captured successfully');
        fetchData();
      }
    } catch (e) {
      console.error('Failed to capture lead:', e);
      toast.error('Failed to capture lead');
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-ux-cyan" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="counselor-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-ux-charcoal">Chat & Counselor Dashboard</h1>
            <p className="text-ux-charcoal/60">
              Manage AI chats and live conversations with students
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Badge className={stats?.is_work_hours ? 'bg-ux-green text-white' : 'bg-gray-400 text-white'}>
              {stats?.is_work_hours ? 'Work Hours Active' : 'Outside Work Hours'}
            </Badge>
            <Button onClick={fetchData} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-ux-charcoal/60">Total Sessions</p>
                  <p className="text-2xl font-bold text-ux-charcoal">{stats?.total_sessions || 0}</p>
                </div>
                <MessageCircle className="w-8 h-8 text-ux-cyan/30" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-ux-charcoal/60">Active Now</p>
                  <p className="text-2xl font-bold text-ux-charcoal">{stats?.active_sessions || 0}</p>
                </div>
                <Users className="w-8 h-8 text-ux-green/30" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-ux-charcoal/60">AI Mode</p>
                  <p className="text-2xl font-bold text-ux-charcoal">{stats?.ai_mode || 0}</p>
                </div>
                <Bot className="w-8 h-8 text-ux-violet/30" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-ux-charcoal/60">Human Mode</p>
                  <p className="text-2xl font-bold text-ux-charcoal">{stats?.human_mode || 0}</p>
                </div>
                <Headphones className="w-8 h-8 text-ux-coral/30" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-ux-charcoal/60">Leads Captured</p>
                  <p className="text-2xl font-bold text-ux-charcoal">{stats?.leads_captured || 0}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-ux-amber/30" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Sessions List */}
          <Card className="lg:col-span-1">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Chat Sessions</CardTitle>
                <Select value={filterMode} onValueChange={setFilterMode}>
                  <SelectTrigger className="w-[120px] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="ai">AI Mode</SelectItem>
                    <SelectItem value="human">Human Mode</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="p-0 max-h-[500px] overflow-y-auto">
              {sessions.length === 0 ? (
                <div className="p-6 text-center text-ux-charcoal/60">
                  <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>No chat sessions yet</p>
                </div>
              ) : (
                <div className="divide-y">
                  {sessions.map((session) => (
                    <div
                      key={session.id}
                      onClick={() => selectSession(session)}
                      className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedSession?.id === session.id ? 'bg-ux-cyan/5 border-l-4 border-ux-cyan' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            session.mode === 'ai' ? 'bg-ux-violet/10' : 'bg-ux-cyan/10'
                          }`}>
                            {session.mode === 'ai' ? (
                              <Bot className="w-4 h-4 text-ux-violet" />
                            ) : (
                              <Headphones className="w-4 h-4 text-ux-cyan" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-sm text-ux-charcoal">
                              {session.visitor_name || 'Visitor'}
                            </p>
                            <p className="text-xs text-ux-charcoal/60">
                              {session.message_count || 0} messages
                            </p>
                          </div>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {session.mode === 'ai' ? 'AI' : 'Human'}
                        </Badge>
                      </div>
                      <p className="text-xs text-ux-charcoal/50">
                        {formatDate(session.updated_at)}
                      </p>
                      {session.destination_interest && (
                        <div className="flex items-center gap-1 mt-1 text-xs text-ux-charcoal/60">
                          <MapPin className="w-3 h-3" />
                          {session.destination_interest}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Chat View */}
          <Card className="lg:col-span-2">
            {selectedSession ? (
              <>
                <CardHeader className="pb-2 border-b">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-ux-cyan/10 flex items-center justify-center">
                        <User className="w-5 h-5 text-ux-cyan" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">
                          {selectedSession.visitor_name || 'Visitor'}
                        </CardTitle>
                        <div className="flex items-center gap-3 text-xs text-ux-charcoal/60">
                          {selectedSession.visitor_email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {selectedSession.visitor_email}
                            </span>
                          )}
                          {selectedSession.visitor_phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {selectedSession.visitor_phone}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!selectedSession.lead_captured && (selectedSession.visitor_email || selectedSession.visitor_phone) && (
                        <Button
                          size="sm"
                          onClick={() => captureLead(selectedSession.id)}
                          className="bg-ux-green hover:bg-ux-green/90"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Capture Lead
                        </Button>
                      )}
                      {selectedSession.lead_captured && (
                        <Badge className="bg-ux-green text-white">Lead Captured</Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>

                {/* Messages */}
                <CardContent className="p-4 h-[350px] overflow-y-auto bg-gray-50">
                  <div className="space-y-4">
                    {selectedSession.messages?.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.sender === 'visitor' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-xl px-4 py-2 ${
                            msg.sender === 'visitor'
                              ? 'bg-ux-cyan text-white'
                              : msg.sender === 'system'
                              ? 'bg-amber-100 text-amber-800'
                              : msg.sender === 'counselor'
                              ? 'bg-ux-violet text-white'
                              : 'bg-white shadow-sm text-ux-charcoal'
                          }`}
                        >
                          <p className="text-sm">{msg.content}</p>
                          <p className={`text-xs mt-1 ${
                            msg.sender === 'visitor' || msg.sender === 'counselor'
                              ? 'text-white/70'
                              : 'text-ux-charcoal/50'
                          }`}>
                            {formatTime(msg.timestamp)}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                </CardContent>

                {/* Reply Input */}
                <div className="p-4 border-t">
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="Type your reply..."
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && sendReply()}
                      className="flex-1"
                    />
                    <Button
                      onClick={sendReply}
                      disabled={!replyMessage.trim()}
                      className="bg-ux-cyan hover:bg-ux-cyan/90"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <CardContent className="h-[500px] flex items-center justify-center">
                <div className="text-center text-ux-charcoal/60">
                  <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>Select a chat session to view conversation</p>
                </div>
              </CardContent>
            )}
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default CounselorDashboard;
